import type { BrandConfig } from '@lds-efsp/brand-config';

/**
 * Makes the HTML document itself carry the active brand: tab title, icons,
 * theme color, description, web fonts and the two font families every element
 * inherits.
 *
 * index.html ships the default brand's values statically, so for that brand
 * this only sets the font variables. A deployment running another brand gets
 * the tags (found by id) pointed at its own files before React renders.
 */
export function applyBrandDocument(brand: BrandConfig, doc: Document = document): void {
  const { document: meta, typography } = brand;

  doc.title = meta.title;
  setAttribute(doc, 'brand-favicon', 'href', meta.favicon);
  setAttribute(doc, 'brand-apple-touch-icon', 'href', meta.appleTouchIcon);
  setAttribute(doc, 'brand-manifest', 'href', meta.manifest);
  setAttribute(doc, 'brand-theme-color', 'content', meta.themeColor);
  setAttribute(doc, 'brand-description', 'content', meta.description);

  const fonts = doc.getElementById('brand-fonts');
  if (fonts) {
    if (meta.fontsHref === null) fonts.remove();
    else if (fonts.getAttribute('href') !== meta.fontsHref) fonts.setAttribute('href', meta.fontsHref);
  }

  // Tailwind's base styles read these two for every element's default font.
  const root = doc.documentElement.style;
  root.setProperty('--font-sans', typography.fontFamily.body);
  root.setProperty('--font-mono', typography.fontFamily.mono);
}

function setAttribute(doc: Document, id: string, name: string, value: string): void {
  const element = doc.getElementById(id);
  if (element && element.getAttribute(name) !== value) element.setAttribute(name, value);
}
